/**
 * NextStepsTab — the five next-step tools as five rows (D-4).
 *
 * RE-HOUSED, NOT REBUILT. Every row's body is the EXISTING card, mounted
 * `frameless` with the exact props ContactRail passes it today (same
 * expressions, same order). These cards send SMS to real patients, fax records
 * releases and book callbacks; re-implementing any of them to fit a new shell
 * would fork endpoints and ids for the sake of a border. The rail owns chrome,
 * the cards own behaviour.
 *
 * Returns a FRAGMENT: the rows are direct children of the rail's Next-steps
 * panel, which carries the one card box and the `divide-y` between rows.
 *
 * Row summaries come only from data the parent ALREADY holds. Schedule can show
 * its gate progress because ContactDetail owns that fetch and passes it down;
 * the other four counts live inside the cards' own fetches, and D-4 adds no
 * callback (and no fetch) to lift them out — a count is not worth a new data
 * path through the page.
 */
import { CalendarClock, ClipboardList, FileSignature, ListTodo, PhoneCall } from 'lucide-react';
import { ContactDetailResponse, ScheduleReadiness, ScheduleReadinessItem } from '../../../types';
import ScheduleReadinessCard from '../../ScheduleReadinessCard';
import SendFormCard from '../../SendFormCard';
import TasksCard from '../../TasksCard';
import RecordsCard from '../../records/RecordsCard';
import CallbacksCard from '../../CallbacksCard';
import DisclosureRow from './DisclosureRow';

export interface NextStepsTabProps {
  detail: ContactDetailResponse;
  reloadToken: number;
  /** Debounced Realtime ping (ContactDetail) — silent rail-card refetches. */
  pingTick?: number;
  onSent: () => void;
  /** Drop a rendered SMS draft into the docked composer (booking + form links). */
  prefillComposer?: (text: string) => void;
  /** Scheduling readiness for the lead's current study (owned by ContactDetail). */
  scheduleReadiness?: ScheduleReadiness | null;
  /** Tick a manual scheduling gate (CRC / investigator / custom sign-off). */
  onToggleGate: (item: ScheduleReadinessItem, done: boolean) => Promise<void>;
}

export default function NextStepsTab({
  detail, reloadToken, pingTick = 0, onSent, prefillComposer, scheduleReadiness, onToggleGate,
}: NextStepsTabProps) {
  const c = detail.contact;

  // ScheduleReadinessCard is the ONE card here that can render nothing
  // (ScheduleReadinessCard.tsx:33 — no study requirements configured). Its row
  // mirrors that condition exactly, because a header row over a null card is a
  // dead row: it opens onto empty space.
  const gates = scheduleReadiness?.items ?? [];
  const showSchedule = Boolean(scheduleReadiness?.configured) && gates.length > 0;
  // Same numbers the card's own badge shows (`progress` is items minus unmet),
  // so the closed row and the open card can never state different progress.
  const scheduleSummary = `${gates.filter((g) => g.done).length}/${gates.length} ready`;

  return (
    <>
      {showSchedule && (
        <DisclosureRow icon={CalendarClock} label="Schedule" summary={scheduleSummary} defaultOpen>
          <ScheduleReadinessCard readiness={scheduleReadiness ?? null} onToggle={onToggleGate} frameless />
        </DisclosureRow>
      )}

      <DisclosureRow icon={ClipboardList} label="Form" defaultOpen>
        {/* Same "considering" study Call Mode opens on — an explicit pin,
            else the study they came in with (resolved server-side). */}
        <SendFormCard
          contact={c}
          primaryStudyId={detail.primary_study?.id || c.primary_study_id || null}
          reloadToken={reloadToken}
          prefillComposer={prefillComposer}
          frameless
        />
      </DisclosureRow>

      <DisclosureRow icon={ListTodo} label="Tasks" defaultOpen>
        <TasksCard contactId={c.id} frameless />
      </DisclosureRow>

      <DisclosureRow icon={FileSignature} label="Records" defaultOpen>
        <RecordsCard contact={c} reloadToken={reloadToken} pingTick={pingTick} onSent={onSent} frameless />
      </DisclosureRow>

      {/* PhoneCall rather than the card's own CalendarClock: Schedule sits two
          rows above with that exact icon, and a flat list whose rows repeat an
          icon is a list you have to read instead of scan. */}
      <DisclosureRow icon={PhoneCall} label="Callbacks" defaultOpen>
        <CallbacksCard
          contact={c}
          maxAttempts={detail.max_attempts || 6}
          reloadToken={reloadToken}
          onSent={onSent}
          prefillComposer={prefillComposer}
          frameless
        />
      </DisclosureRow>
    </>
  );
}
